import { useState } from 'react';
import { Bell, CheckCircle2, AlertTriangle, Loader2, BellOff, Lock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { registerAuthenticatedPushToken } from '../../security/pushApi';

const API = import.meta.env.VITE_API_URL || '/api/v1';

// Firebase config (VITE env)
const FIREBASE_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
};

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string;

/**
 * Inicializa Firebase App + Messaging usando import dinamico para evitar
 * problemas de tipo com verbatimModuleSyntax e versoes do Firebase.
 */
async function getFCMToken(): Promise<string> {
  // Import dinamico - funciona com qualquer versao do firebase
  const { initializeApp, getApps } = await import('firebase/app');
  const { getMessaging, getToken } = await import('firebase/messaging');

  const app = getApps().length > 0 ? getApps()[0] : initializeApp(FIREBASE_CONFIG);
  const messaging = getMessaging(app);

  // Reutiliza o Service Worker ativo (/tile-cache-sw.js)
  const registration = await navigator.serviceWorker.ready;
  const token = await getToken(messaging, { 
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration
  });
  if (!token) throw new Error('Token FCM nao obtido. Verifique VAPID Key.');
  return token;
}

type Status = 'idle' | 'requesting' | 'registering' | 'success' | 'denied' | 'error';

interface Props {
  onSuccess?: (fcmToken: string) => void;
  onSkip?: () => void;
  sessionId?: string; // se fornecido, registra na sessao Redis em vez do usuario
}

/**
 * PushPermissionRequest - Solicita permissao de notificacao push e registra
 * o token FCM no backend para alertas de seguranca.
 *
 * Usa import dinamico do Firebase para compatibilidade com verbatimModuleSyntax.
 *
 * Visual: segue o padrao glass-card do fluxo de bonding.
 * Notificacao: OBRIGATORIA no fluxo de ativacao.
 */
export function PushPermissionRequest({ onSuccess, onSkip, sessionId }: Props) {
  const [status, setStatus] = useState<Status>(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && typeof (window as any).Notification !== 'undefined' && (window as any).Notification?.permission === 'denied') {
      return 'denied';
    }
    return 'idle';
  });
  const [error, setError] = useState('');
  const isSupported = typeof window !== 'undefined' && 'Notification' in window && typeof (window as any).Notification !== 'undefined' && typeof (window as any).Notification?.requestPermission === 'function';

  const activar = async () => {
    setStatus('requesting');
    setError('');

    try {
      if (!isSupported) {
        // Fallback para navegadores/dispositivos sem suporte (iOS Safari, WebViews, frame privado)
        setStatus('registering');
        const fcmToken = 'mock-fcm-token-unsupported';

        if (sessionId) {
          const token = (localStorage.getItem('ac_token') || '');
          const response = await fetch(`${API}/security/session/${sessionId}/push-register`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ fcmToken }),
          });
          if (!response.ok) {
            throw new Error('Falha ao registrar notificacoes na sessao de seguranca.');
          }
        } else {
          const token = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
          await registerAuthenticatedPushToken(
            { fcmToken, plataforma: 'WEB', tipo: 'profile_manual' },
            { bearerToken: token },
          );
        }

        setStatus('success');
        localStorage.setItem('ac_fcm_token', fcmToken);
        onSuccess?.(fcmToken);
        return;
      }

      const permission = await (window as any).Notification.requestPermission();

      if (permission !== 'granted') {
        setStatus('denied');
        return;
      }

      setStatus('registering');
      const fcmToken = await getFCMToken();

      // ── Registrar listener de foreground ──
      // Quando o push chega com a pagina aberta, o SW NAO mostra notificacao.
      // Precisamos do onMessage() do Firebase SDK para interceptar e exibir.
      try {
        const { getMessaging, onMessage } = await import('firebase/messaging');
        const { getApps } = await import('firebase/app');
        const app = getApps()[0];
        if (app) {
          const messaging = getMessaging(app);
          onMessage(messaging, (payload) => {
            console.log('[FCM Foreground]', payload);
            // Exibir via Notification API nativa se suportado
            if (payload.notification && typeof (window as any).Notification !== 'undefined') {
              new (window as any).Notification(payload.notification.title || 'Aluno Conectado', {
                body: payload.notification.body || '',
                icon: '/icons/icon-192x192.png',
              });
            }
          });
          console.log('[FCM] Foreground listener registrado');
        }
      } catch (fgErr) {
        console.warn('[FCM] Foreground listener falhou:', fgErr);
      }

      // Se sessionId fornecido, registra na sessao (bonding)
      if (sessionId) {
        const token = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
        try {
          const response = await fetch(`${API}/security/session/${sessionId}/push-register`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ fcmToken }),
          });
          if (!response.ok) {
            await registerAuthenticatedPushToken(
              { fcmToken, plataforma: 'WEB', tipo: 'profile_manual' },
              { bearerToken: token },
            );
          }
        } catch {
          await registerAuthenticatedPushToken(
            { fcmToken, plataforma: 'WEB', tipo: 'profile_manual' },
            { bearerToken: token },
          );
        }
      } else {
        const token = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
        await registerAuthenticatedPushToken(
          { fcmToken, plataforma: 'WEB', tipo: 'profile_manual' },
          { bearerToken: token },
        );
      }

      setStatus('success');
      localStorage.setItem('ac_fcm_token', fcmToken);
      onSuccess?.(fcmToken);
    } catch (e: unknown) {
      const msg = (e as Error)?.message ?? '';
      if (
        /permission denied/i.test(msg) ||
        /Registration failed/i.test(msg) ||
        (typeof window !== 'undefined' && 'Notification' in window && (window as any).Notification?.permission === 'denied')
      ) {
        setStatus('denied');
        setError('');
      } else {
        setError(msg || 'Erro ao ativar notificações.');
        setStatus('error');
      }
    }
  };

  // ── SUCESSO ──────────────────────────────────────────
  if (status === 'success') {
    return (
      <div className="animate-fade-in-up" style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        padding: '32px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden',
        borderRadius: 20, background: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 8px 32px rgba(108, 92, 231, 0.08)',
        width: '100%'
      }}>
        <div style={{
          position: 'absolute', top: -40, left: '50%', transform: 'translateX(-50%)',
          width: 200, height: 200, background: 'rgba(108, 92, 231, 0.08)',
          filter: 'blur(50px)', zIndex: 0
        }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{
            width: 96, height: 96, borderRadius: '50%', margin: '0 auto 16px',
            background: 'linear-gradient(135deg, rgba(108, 92, 231, .1), rgba(90, 79, 207, .05))',
            border: '2px solid rgba(108, 92, 231, 0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 30px rgba(108, 92, 231, 0.1), inset 0 0 15px rgba(108, 92, 231, 0.05)',
          }}>
            <CheckCircle2 size={48} strokeWidth={1.5} style={{ color: '#6c5ce7' }} />
          </div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
            Notificações Ativadas!
          </h2>
          <p style={{ color: '#64748b', fontSize: '.85rem', margin: 0, maxWidth: 240, lineHeight: 1.5 }}>
            Você receberá alertas de segurança neste dispositivo. Continuando...
          </p>
        </div>
      </div>
    );
  }

  // ── PERMISSAO NEGADA OU BLOQUEADA NO NAVEGADOR ──────────────────────────────────
  const isBrowserDenied = typeof window !== 'undefined' && 'Notification' in window && (window as any).Notification?.permission === 'denied';

  if (status === 'denied' || isBrowserDenied) {
    const handleRecheckPermission = async () => {
      if (typeof window !== 'undefined' && 'Notification' in window && typeof (window as any).Notification !== 'undefined') {
        const perm = (window as any).Notification.permission;
        if (perm === 'granted') {
          await activar();
        } else if (perm === 'default') {
          setStatus('idle');
          setError('');
        } else {
          toast.error('Notificações ainda estão bloqueadas no navegador. Altere para "Permitir" no ícone de cadeado do site.');
        }
      }
    };

    return (
      <div className="animate-fade-in-up" style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        padding: '28px 20px', textAlign: 'center', position: 'relative', overflow: 'hidden',
        borderRadius: 20, background: '#ffffff',
        border: '1px solid #fee2e2',
        boxShadow: '0 8px 32px rgba(239, 68, 68, 0.08)',
        width: '100%'
      }}>
        <div style={{
          position: 'absolute', top: -40, left: '50%', transform: 'translateX(-50%)',
          width: 200, height: 200, background: 'rgba(239, 68, 68, 0.06)',
          filter: 'blur(50px)', zIndex: 0
        }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', margin: '0 auto 14px',
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(225, 29, 72, 0.05))',
            border: '2px solid rgba(239, 68, 68, 0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 25px rgba(239, 68, 68, 0.12)',
          }}>
            <BellOff size={40} strokeWidth={1.5} style={{ color: '#ef4444' }} />
          </div>
          
          <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
            Notificações Desativadas
          </h2>
          
          <p style={{ color: '#475569', fontSize: '.84rem', margin: '0 0 14px', maxWidth: 320, lineHeight: 1.5 }}>
            As notificações estão desativadas neste navegador. Siga as orientações abaixo para ativar:
          </p>

          {/* Passo a Passo */}
          <div style={{
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12,
            padding: '12px 14px', textAlign: 'left', width: '100%', maxWidth: 340,
            marginBottom: 16, fontSize: '.78rem', color: '#334155', lineHeight: 1.5
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Lock size={14} style={{ color: '#6366f1' }} /> Como ativar no seu navegador:
            </div>
            <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <li>Clique no <strong>ícone de cadeado / configurações</strong> ao lado do endereço do site (na barra do navegador).</li>
              <li>Localize a opção <strong>Notificações</strong> e altere para <strong>Permitir</strong>.</li>
              <li>Clique no botão <strong>Verificar Novamente</strong> abaixo.</li>
            </ol>
          </div>

          <button
            onClick={handleRecheckPermission}
            style={{
              width: '100%', padding: '12px 20px', borderRadius: 14, border: 'none',
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              color: '#fff', fontSize: '.9rem', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 14px rgba(99, 102, 241, 0.25)',
              transition: 'all .3s cubic-bezier(.16,1,.3,1)',
            }}
          >
            <RefreshCw size={16} /> Verificar Novamente
          </button>
        </div>
      </div>
    );
  }

  // ── TELA PRINCIPAL — SOLICITAR PERMISSAO ──────────────
  return (
    <div className="animate-fade-in-up" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
      padding: '32px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden',
      borderRadius: 20, background: '#ffffff',
      border: '1px solid #e2e8f0',
      boxShadow: '0 8px 32px rgba(108, 92, 231, 0.08)',
      width: '100%'
    }}>
      {/* Glow background (Purple) */}
      <div style={{
        position: 'absolute', top: -40, left: '50%', transform: 'translateX(-50%)',
        width: 200, height: 200, background: 'rgba(108, 92, 231, 0.08)',
        filter: 'blur(50px)', zIndex: 0
      }} />

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        {/* Animated Bell Icon */}
        <div style={{
          width: 96, height: 96, borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(108, 92, 231, 0.1), rgba(90, 79, 207, 0.05))',
          border: '2px solid rgba(108, 92, 231, 0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 0 30px rgba(108, 92, 231, 0.1), inset 0 0 15px rgba(108, 92, 231, 0.05)',
          animation: 'sw-purple-pulse 2s ease-in-out infinite',
          margin: '0 auto 16px'
        }}>
          <Bell size={48} strokeWidth={1.5} style={{ color: '#6c5ce7' }} />
        </div>

        <div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
            Ativar Notificações
          </h2>
          <p style={{ color: '#64748b', fontSize: '.85rem', margin: 0, maxWidth: 240, lineHeight: 1.5 }}>
            Vamos enviar um <strong style={{ color: '#0f172a' }}>código de verificação</strong> via notificação no seu celular.
            <br /><br />
            Para receber, você precisa permitir as notificações neste dispositivo.
          </p>
        </div>

        {error && (
          <div style={{
            marginTop: 16, padding: '8px 16px', borderRadius: 10,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
            fontSize: '.78rem', color: '#f87171', maxWidth: 260,
          }}>
            <AlertTriangle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> 
            {error}
          </div>
        )}

        {!isSupported && (
          <div style={{
            marginTop: 16, padding: '8px 16px', borderRadius: 10,
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
            fontSize: '.78rem', color: '#d97706', maxWidth: 260,
          }}>
            <AlertTriangle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> 
            Notificações push não suportadas por este navegador. O fluxo será simulado para permitir prosseguir.
          </div>
        )}

        <button
          onClick={activar}
          disabled={status === 'requesting' || status === 'registering'}
          style={{
            marginTop: 20, width: '100%', padding: '12px 20px', borderRadius: 14, border: 'none',
            background: (status === 'requesting' || status === 'registering') 
               ? '#f1f5f9' 
               : 'linear-gradient(135deg, #6c5ce7, #5a4fcf)',
            color: (status === 'requesting' || status === 'registering') ? '#94a3b8' : '#fff', 
            fontSize: '.92rem', fontWeight: 700, 
            cursor: (status === 'requesting' || status === 'registering') ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: (status === 'requesting' || status === 'registering') ? 'none' : '0 4px 14px rgba(108, 92, 231, 0.25)',
            transition: 'all .3s cubic-bezier(.16,1,.3,1)',
          }}
          onMouseDown={(e) => { if (status === 'idle' || status === 'error') e.currentTarget.style.transform = 'scale(0.97)' }}
          onMouseUp={(e) => { if (status === 'idle' || status === 'error') e.currentTarget.style.transform = 'scale(1)' }}
        >
          {status === 'requesting' && (
            <><Loader2 size={18} style={{ animation: 'spin .8s linear infinite' }} /> Solicitando permissão...</>
          )}
          {status === 'registering' && (
            <><Loader2 size={18} style={{ animation: 'spin .8s linear infinite' }} /> Registrando dispositivo...</>
          )}
          {(status === 'idle' || status === 'error') && (
            <><Bell size={18} /> {isSupported ? 'Permitir e Enviar Código' : 'Simular Ativação (Não Suportado)'}</>
          )}
        </button>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes sw-purple-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 30px rgba(108, 92, 231, 0.1), inset 0 0 15px rgba(108, 92, 231, 0.05); }
          50% { transform: scale(1.05); box-shadow: 0 0 40px rgba(108, 92, 231, 0.25), inset 0 0 20px rgba(108, 92, 231, 0.1); }
        }
      `}</style>
    </div>
  );
}
